import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import client from '../apollo';

const useGraphQLQuery = ({
  query,
  variables,
  fetchPolicy = 'network-only',
  skip = false,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // UseRef will keep value persist when components re-render
  const isMounted = useRef(true);

  const fetchData = useCallback(async() => {
    if (!isMounted.current) return;
    // A skipped query reports the same state as one that has not run yet.
    // Keeping the previous `data`/`error` here would make `{ loading: false }`
    // mean both "here is the result" and "here is the result of a query the
    // caller has since skipped", which the caller cannot tell apart.
    if (skip) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await client.query({ query, variables, fetchPolicy });
      if (isMounted.current) {
        setData(result.data);
        setError(null);
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err);
        setData(null);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [query, JSON.stringify(variables), skip]);

  useEffect(() => {
    isMounted.current = true;
    fetchData();

    // Clean up function, safely prevent state updates, memory leaks after the component has unmounted
    return () => {
      isMounted.current = false;
    };
  }, [fetchData]);

  return {
    data, loading, error, refetch: fetchData,
  };
};

export default useGraphQLQuery;
